import { HeartHandshake, ShieldCheck, BookOpen, Brain, Sun, Moon, ClipboardCheck } from "lucide-react";
import PageHero, { NextPage } from "@/components/PageHero";
import { FadeUp, StaggerGroup, StaggerItem } from "@/components/Reveal";

const STATS = [
  { value: "3:1", label: "Baseline care staffing", color: "#FF508D" },
  { value: "4:1", label: "Risk-led capacity", color: "#FF9137" },
  { value: "2 + 1", label: "Two waking, one sleep-in", color: "#696DFF" },
];

const PILLARS = [
  {
    title: "Relational care",
    copy: "Staff use trauma-informed, attachment-aware and restorative practice. Predictable routines, co-regulation and positive relationships help the child feel safe and build emotional regulation.",
    Icon: HeartHandshake,
    color: "#FF508D",
    span: "sm:col-span-4",
  },
  {
    title: "Safeguarding",
    copy: "Individual risk assessments, missing-from-care protocols, online safety, medication controls and multi-agency plans are kept under review. Concerns are reported and escalated without delay.",
    Icon: ShieldCheck,
    color: "#696DFF",
    span: "sm:col-span-4",
  },
  {
    title: "Education & SEND",
    copy: "Staff implement EHCP and PEP strategies, make reasonable adjustments, adapt communication and work with the school or provider, SENCO and Virtual School to sustain learning.",
    Icon: BookOpen,
    color: "#5CFFAF",
    span: "sm:col-span-4",
  },
  {
    title: "Health & therapy",
    copy: "Health needs are coordinated with relevant services. Jazzino Davies, our child therapist, supports formulation, consultation and direct therapeutic work where agreed in the child's plan.",
    Icon: Brain,
    color: "#FFB019",
    span: "sm:col-span-12",
  },
];

export default function CareSupport() {
  return (
    <div data-testid="page-care-support">
      <PageHero
        testid="care-hero"
        eyebrow="How support is delivered"
        title={[
          "Care, staffing",
          <>
            and <em className="text-rainbow italic">safeguards</em>
          </>,
        ]}
        lead="A consistent, professionally supervised team keeps the home safe, calm and focused on the child — day and night."
      />

      {/* Stat trio */}
      <section className="container-x" data-testid="ratios-section">
        <StaggerGroup className="grid grid-cols-1 gap-6 sm:grid-cols-3">
          {STATS.map(({ value, label, color }) => (
            <StaggerItem key={value}>
              <div className="card edge-rainbow h-full p-8 transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-[0_24px_45px_-28px_rgba(45,55,72,0.45)]" data-testid={`ratio-card-${value.replace(/[\s:]/g, "")}`}>
                <p className="font-display text-6xl tracking-tight text-ink" style={{ color }} data-testid={`ratio-value-${value.replace(/[\s:]/g, "")}`}>
                  {value}
                </p>
                <p className="mt-3 text-xs font-extrabold uppercase tracking-[0.18em] text-mute">{label}</p>
              </div>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </section>

      {/* Staffing model */}
      <section className="container-x py-16 sm:py-20 lg:py-24" data-testid="staffing-model">
        <FadeUp>
          <p className="eyebrow">Staffing model</p>
          <h2 className="mt-4 max-w-2xl font-display text-3xl tracking-tight text-ink sm:text-4xl" data-testid="staffing-title">
            The team around the child, day and night
          </h2>
        </FadeUp>
        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          <FadeUp>
            <div className="card h-full p-8 sm:p-10" data-testid="day-model-card">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-coral/10 text-coral">
                <Sun className="h-6 w-6" strokeWidth={1.5} />
              </span>
              <h3 className="mt-6 font-display text-2xl text-ink" data-testid="day-model-title">Daytime</h3>
              <p className="mt-4 text-base leading-relaxed text-mute" data-testid="day-model-copy">
                The planned daytime model is three care staff supporting the child. A fourth care
                worker can be deployed when a current risk assessment, care plan or court-authorised
                arrangement identifies increased need. The Registered Manager works on a separate
                management rota and is not counted within the child's operational staffing ratio.
              </p>
            </div>
          </FadeUp>
          <FadeUp delay={0.12}>
            <div className="card h-full p-8 sm:p-10" data-testid="night-model-card">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-sky/10 text-sky">
                <Moon className="h-6 w-6" strokeWidth={1.5} />
              </span>
              <h3 className="mt-6 font-display text-2xl text-ink" data-testid="night-model-title">Overnight</h3>
              <p className="mt-4 text-base leading-relaxed text-mute" data-testid="night-model-copy">
                The planned team is one Night Manager and two Residential Support Workers: the Night
                Manager and one Residential Support Worker remain awake, while the second Residential
                Support Worker undertakes the sleep-in duty.
              </p>
            </div>
          </FadeUp>
        </div>
        <FadeUp delay={0.15}>
          <p className="mt-8 flex max-w-3xl items-start gap-3 text-sm leading-relaxed text-mute" data-testid="staffing-review-note">
            <ClipboardCheck className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} />
            Staffing is reviewed following incidents, changes in presentation, professional advice
            and statutory review.
          </p>
        </FadeUp>
      </section>

      {/* Practice pillars bento */}
      <section className="container-x" data-testid="pillars-section">
        <FadeUp>
          <p className="eyebrow">Practice pillars</p>
          <h2 className="mt-4 max-w-2xl font-display text-3xl tracking-tight text-ink sm:text-4xl" data-testid="pillars-title">
            How care is delivered every day
          </h2>
        </FadeUp>
        <StaggerGroup className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-12">
          {PILLARS.map(({ title, copy, Icon, color, span }) => (
            <StaggerItem key={title} className={span}>
              <div className="card group h-full p-8 transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-[0_24px_45px_-28px_rgba(45,55,72,0.45)]" data-testid={`pillar-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="flex items-center gap-4">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-sand text-ink">
                    <Icon className="h-6 w-6" strokeWidth={1.5} />
                  </span>
                  <span className="dot" style={{ background: color }} aria-hidden="true" />
                </div>
                <h3 className="mt-6 font-display text-2xl text-ink">{title}</h3>
                <p className="mt-3 text-base leading-relaxed text-mute">{copy}</p>
              </div>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </section>

      {/* Safeguarding & DoLS */}
      <section className="container-x" data-testid="safeguarding-section">
        <FadeUp>
          <div className="relative overflow-hidden rounded-[2.5rem] bg-sand px-7 py-14 sm:px-14 sm:py-16">
            <div className="flex items-start gap-6">
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white text-ink">
                <ShieldCheck className="h-7 w-7" strokeWidth={1.5} />
              </span>
              <div>
                <p className="eyebrow">Safeguarding</p>
                <h2 className="mt-4 font-display text-2xl tracking-tight text-ink sm:text-3xl" data-testid="safeguarding-title">
                  Deprivation of Liberty, done lawfully and with kindness
                </h2>
                <p className="mt-5 max-w-4xl text-lg leading-relaxed text-ink/90" data-testid="safeguarding-copy">
                  At Infinity &amp; Beyond Care, we provide specialist support for boys who are subject to a
                  Deprivation of Liberty order from the court. DoLS is a legal process that ensures any
                  necessary restrictions on a young person's freedom are authorised, reviewed, and lawful —
                  always to keep them safe and protect their rights. Our home delivers that care with
                  kindness, dignity, and consistency, fully following what the court has ordered.
                </p>
              </div>
            </div>
          </div>
        </FadeUp>
      </section>

      {/* Child's voice */}
      <section className="container-x py-16 sm:py-20 lg:py-24" data-testid="voice-section">
        <FadeUp>
          <div className="relative overflow-hidden rounded-[2.5rem] bg-sand px-7 py-14 sm:px-14 sm:py-20">
            <div className="absolute right-8 top-8 opacity-[0.08]" aria-hidden="true">
              <HeartHandshake className="h-40 w-40 text-ink" strokeWidth={1.5} />
            </div>
            <p className="eyebrow">The child's voice and rights</p>
            <p className="mt-6 max-w-3xl font-display text-2xl leading-snug text-ink sm:text-3xl lg:text-4xl" data-testid="voice-statement">
              The child will be supported to understand his care, court-authorised arrangements,
              advocacy and complaints routes.
            </p>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-mute" data-testid="voice-copy">
              His views will inform daily decisions, activities, reviews and care planning. Family
              and significant relationships will be promoted where this is safe and consistent with
              the care plan and court order.
            </p>
          </div>
        </FadeUp>
      </section>

      <div className="container-x pb-4">
        <NextPage
          to="/local-area"
          title="Local area and community resources"
          label="Transport, shopping, leisure and youth support around the home"
          testid="next-local-area"
        />
      </div>
    </div>
  );
}
